import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Company, CompanyInsert, CompanyUpdate } from "@/hooks/useCompanies";
import { Database } from "@/integrations/neon/types";

type CompanyType = Database["public"]["Enums"]["company_type"];

const companyTypes: { value: CompanyType; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "provider", label: "Provider" },
  { value: "main_company", label: "Main Company" },
];

const companySchema = z.object({
  number: z.string().max(50, "Number is too long").nullable().optional(),
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
  invoice_address: z
    .string()
    .max(1000, "Address is too long")
    .nullable()
    .optional(),
  language: z.string().nullable().optional(),
  types: z.array(z.enum(["customer", "provider", "main_company"])).default([]),
  is_main_company: z.boolean().default(false),
});

type CompanyFormValues = z.infer<typeof companySchema>;

interface CompanyFormProps {
  company?: Company | null;
  onSubmit: (data: CompanyInsert | CompanyUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CompanyForm({
  company,
  onSubmit,
  onCancel,
  isLoading,
}: CompanyFormProps) {
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      number: company?.number ?? "",
      name: company?.name ?? "",
      invoice_address: company?.invoice_address ?? "",
      language: company?.language ?? null,
      types: (company?.types as CompanyType[]) ?? [],
      is_main_company: company?.is_main_company ?? false,
    },
  });

  const handleSubmit = (values: CompanyFormValues) => {
    onSubmit({
      number: values.number || null,
      name: values.name,
      invoice_address: values.invoice_address || null,
      language: values.language || null,
      types: values.types,
      is_main_company: values.is_main_company,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Unique identifier"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="invoice_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invoice Address</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter invoice address"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Language</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value ?? undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="types"
          render={() => (
            <FormItem>
              <FormLabel>Company Types</FormLabel>
              <div className="flex flex-wrap gap-4">
                {companyTypes.map((type) => (
                  <FormField
                    key={type.value}
                    control={form.control}
                    name="types"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(type.value)}
                            onCheckedChange={(checked) => {
                              const current = field.value || [];
                              if (checked) {
                                field.onChange([...current, type.value]);
                              } else {
                                field.onChange(
                                  current.filter((v) => v !== type.value),
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          {type.label}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? "Saving..."
              : company
                ? "Update Company"
                : "Create Company"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
